import { GameMode } from "./GameMode.js";
import { Ruleset } from "./Ruleset.js";
import { BotType } from "./BotType.js";
import { LobbySettings } from "./LobbySettings.js";
import type { GameSettings } from "../../royalur/model/GameSettings.js";
import { isJsonDict, readJsonEnumOrElse, readNullableJsonDict } from "../../util/json.js";
import { RoyalUrJsonNotation } from "./royalur/RoyalUrJsonNotation.js";


/**
 * Used when we know all the settings of a game.
 */
export class KnownLobbySettings extends LobbySettings {
    static readonly DEFAULT = new KnownLobbySettings(
        GameMode.COMPUTER,
        Ruleset.FINKEL,
        Ruleset.FINKEL.getSettings(),
        BotType.EASY,
    );

    private readonly mode: GameMode;
    private readonly ruleset: Ruleset;
    private readonly customGameSettings: GameSettings;
    private readonly botType: BotType;

    constructor(
        mode: GameMode,
        ruleset: Ruleset,
        customGameSettings: GameSettings,
        botType: BotType,
    ) {
        super(true, null);
        this.mode = mode;
        this.ruleset = ruleset;
        this.customGameSettings = customGameSettings;
        this.botType = botType;
    }

    toJSON(): string {
        const jsonNotation = new RoyalUrJsonNotation();
        return JSON.stringify({
            mode: this.mode.getID(),
            gameSettings: this.ruleset.getID(),
            customGameSettings: jsonNotation.writeGameSettings(this.customGameSettings),
            difficulty: this.botType.getID(),
        });
    }

    static fromJSON(json: string, defaultValues: KnownLobbySettings): KnownLobbySettings {
        try {
            const values = JSON.parse(json);
            if (!isJsonDict(values))
                return defaultValues;

            const mode: GameMode = readJsonEnumOrElse(
                values, "mode",
                GameMode.PARSING_MAP,
                defaultValues.getMode(),
            );

            const ruleset: Ruleset = readJsonEnumOrElse(
                values, "gameSettings",
                Ruleset.PARSING_MAP,
                defaultValues.getRuleset(),
            );

            let customGameSettings = defaultValues.getCustomGameSettings();
            const customGameSettingsJSON = readNullableJsonDict(values, "customGameSettings");
            if (customGameSettingsJSON) {
                const jsonNotation = new RoyalUrJsonNotation();
                customGameSettings = jsonNotation.readGameSettings(customGameSettingsJSON);
            }

            const difficulty: BotType = readJsonEnumOrElse(
                values, "difficulty",
                BotType.PARSING_MAP,
                defaultValues.getBotType(),
            );

            return new KnownLobbySettings(
                mode,
                ruleset,
                customGameSettings,
                difficulty,
            );
        } catch (e) {
            console.error("Error parsing preferred game setup settings", e);
            return defaultValues;
        }
    }

    override getMode(): GameMode {
        return this.mode;
    }

    override getRuleset(): Ruleset {
        return this.ruleset;
    }

    override getGameSettings(): GameSettings {
        return (
            this.ruleset.hasSettings()
                ? this.ruleset.getSettings()
                : this.customGameSettings
        );
    }

    override getCustomGameSettings(): GameSettings {
        return this.customGameSettings;
    }

    override getBotType(): BotType {
        return this.botType;
    }

    withMode(mode: GameMode): KnownLobbySettings {
        return new KnownLobbySettings(
            mode, this.ruleset, this.customGameSettings, this.botType,
        );
    }

    withRuleset(ruleset: Ruleset): KnownLobbySettings {
        return new KnownLobbySettings(
            this.mode, ruleset, this.customGameSettings, this.botType,
        );
    }

    withCustomGameSettings(gameSettings: GameSettings): KnownLobbySettings {
        return new KnownLobbySettings(
            this.mode, this.ruleset, gameSettings, this.botType,
        );
    }

    withBotType(botType: BotType): KnownLobbySettings {
        return new KnownLobbySettings(
            this.mode, this.ruleset, this.customGameSettings, botType,
        );
    }

    override equals(other: LobbySettings): boolean {
        if (!(other instanceof KnownLobbySettings))
            return false;

        return this.mode === other.mode
            && this.ruleset === other.ruleset
            && this.botType === other.botType;
    }
}
